import * as yup from 'yup';
import { ExternalLink } from 'react-feather';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';

import { Event } from '../Settings/helpers';
import { TranslatableEditor } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { replacementKeys } from '../Settings/EditEventConfirmationMessagesForm';
import { translationsForAttribute } from '../../../Translations/helpers';
import GetEventSettingsQuery from '../Settings/GetEventSettingsQuery';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import config from '../../../config';
import useEditEventMutation from '../Settings/useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

export interface ResaleSettingsPageProps {
  eventId: string;
}

const ResaleSettingsPage = ({ eventId }: ResaleSettingsPageProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  const { data } = useQuery(GetEventSettingsQuery, {
    variables: {
      project_id: project.id,
      event_id: eventId,
    },
  });

  const event = data?.project.event;

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.SectionHeading>
            {t('resale')}
          </UI.SectionHeading>

          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.Legend sc={{ mr: 3 }}>
              <UI.Delimit>
                {event.enable_public_resale && (
                  <UI.Span>
                    {t('n_tickets_available', {
                      value: formatNumber(event.cached_registrations_for_sale_count),
                      count: event.cached_registrations_for_sale_count,
                    })}
                  </UI.Span>
                )}
                {(event.enable_public_resale || event.sold_registrations_count > 0) && (
                  <UI.Span>
                    {t('n_tickets_sold', {
                      value: formatNumber(event.sold_registrations_count),
                      count: event.sold_registrations_count,
                    })}
                  </UI.Span>
                )}
              </UI.Delimit>
            </UI.Legend>

            <UI.A
              href={event.resale_url}
              target="_blank"
              rel="noopener noreferer"
              sc={{ strong: true }}
            >
              {t('view_public_resale_page')}
            </UI.A>
          </UI.FlexContainer>
        </UI.FlexContainer>

        <UI.FadeIn sc={{ when: !event.enable_private_resale && !event.enable_public_resale }}>
          <ResaleSettingsForm event={event} />
        </UI.FadeIn>
      </UI.GridContainer>
    </UI.Section>
  );
};

const editResaleSettingsSchema = yup.object();

export interface ResaleSettingsFormProps {
  event: Event;
}

const ResaleSettingsForm = ({ event }: ResaleSettingsFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const { locale } = useLocale();

  const getValues = (event: Event) => ({
    enable_private_resale: event.enable_private_resale,
    enable_public_resale: event.enable_public_resale,
    resale_description: event.resale_description,
    resale_description_translations: translationsForAttribute(event.translations, 'resale_description'),
    use_default_confirmation_text: !event.resale_confirmation_text,
    confirmation_text: event.resale_confirmation_text,
    confirmation_text_translations: translationsForAttribute(event.translations, 'resale_confirmation_text'),
    use_default_confirmation_mail: !event.resale_confirmation_mail_text,
    confirmation_mail_text: event.resale_confirmation_mail_text,
    confirmation_mail_text_translations: translationsForAttribute(event.translations, 'resale_confirmation_mail_text'),
    ticket_listed_mail_text: event.resale_ticket_listed_mail_text,
    ticket_listed_mail_text_translations: translationsForAttribute(
      event.translations,
      'resale_ticket_listed_mail_text',
    ),
    resellable_tickets: event.tickets.filter((ticket) => ticket.allow_resale).map((ticket) => ticket.id),
    resellable_products: event.products.filter((product) => product.allow_resale).map((product) => product.id),
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const toggleDefaultConfirmationText = () => {
    form.set((values) => ({ ...values, use_default_confirmation_text: !values.use_default_confirmation_text }));
  };

  const toggleDefaultConfirmationMail = () => {
    form.set((values) => ({ ...values, use_default_confirmation_mail: !values.use_default_confirmation_mail }));
  };

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      resale: {
        enable_private_resale: form.values.enable_private_resale,
        enable_public_resale: form.values.enable_public_resale,
        description: form.values.resale_description,
        description_translations: form.values.resale_description_translations,
        confirmation_text: !form.values.use_default_confirmation_text ? form.values.confirmation_text : null,
        confirmation_text_translations: !form.values.use_default_confirmation_text
          ? form.values.confirmation_text_translations : [],
        confirmation_mail_text: !form.values.use_default_confirmation_mail ? form.values.confirmation_mail_text : null,
        confirmation_mail_text_translations: !form.values.use_default_confirmation_mail
          ? form.values.confirmation_mail_text_translations : [],
        ticket_listed_mail_text: form.values.ticket_listed_mail_text,
        ticket_listed_mail_text_translations: form.values.ticket_listed_mail_text_translations,
        resellable_tickets: form.values.resellable_tickets,
        resellable_products: form.values.resellable_products,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editResaleSettingsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const resellableTickets = event.tickets.filter((ticket) => ticket.allowable_for_resale);
  const resellableProducts = event.products.filter((product) => product.allowable_for_resale);

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.Card sc={{ padding: [4, 6] }}>
        <UI.GridContainer sc={{ columns: ['1fr', '1fr 2fr'], gutter: [1, 1.5] }}>
          <UI.Div>
            <UI.H4 sc={{ mb: 1 }}>
              {t('ticket_resale')}
            </UI.H4>
            <UI.InputDescription sc={{ mb: 1 }}>
              {t('resale_description')}
            </UI.InputDescription>
            <UI.InputDescription>
              <UI.A
                href={config.resale.infoUrl[locale]}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('about_resale')}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.A>
            </UI.InputDescription>
          </UI.Div>

          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            <UI.InputGroup
              sc={{
                valid: !errors.enable_private_resale && !errors.enable_public_resale,
                touched: form.touched.enable_private_resale || form.touched.enable_public_resale,
              }}
            >
              <UI.FormGrid sc={{ gutter: 0.25 }}>
                <UI.Checkbox
                  {...form.checkbox('enable_private_resale')}
                  sc={{ box: true }}
                >
                  <UI.Strong sc={{ block: true }}>
                    {t('enable_private_resale_label')}
                  </UI.Strong>
                  {t('enable_private_resale_description')}
                </UI.Checkbox>
                <UI.Checkbox
                  {...form.checkbox('enable_public_resale')}
                  checked={form.values.enable_public_resale}
                  sc={{ box: true }}
                >
                  <UI.Strong sc={{ block: true }}>
                    {t('enable_public_resale_label')}
                  </UI.Strong>
                  {t('enable_public_resale_description')}
                </UI.Checkbox>
                <UI.ErrorMessages attribute={t('enable_ticket_resale')} errors={errors.enable_private_resale} />
                <UI.ErrorMessages attribute={t('enable_ticket_resale')} errors={errors.enable_public_resale} />
              </UI.FormGrid>
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.description, touched: form.touched.description }}>
              <UI.InputLabel id="ResaleDescriptionLabel">
                {t('description')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_description_description')}
              </UI.InputDescription>
              <TranslatableEditor
                form={form}
                name="resale_description"
                defaultLocale={event.locale}
                title={t('description')}
                attributes={{
                  'aria-labelledby': 'ResaleDescriptionLabel',
                }}
              />
              <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
            </UI.InputGroup>

            <UI.HR />

            <UI.InputGroup sc={{ valid: !errors.resellable_tickets, touched: form.touched.resellable_tickets }}>
              <UI.InputLabel>
                {t('resellable_tickets')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resellable_tickets_description')}
              </UI.InputDescription>
              {resellableTickets.map((ticket) => (
                <UI.Checkbox
                  {...form.checklist('resellable_tickets', ticket.id)}
                  key={ticket.id}
                >
                  {ticket.title}
                </UI.Checkbox>
              ))}
              {resellableTickets.length === 0 && (
                <UI.Div sc={{ mt: 2 }}>
                  <Trans i18nKey="no_tickets_created">
                    <UI.Link to={`/${project.id}/events/${event.id}/tickets`} />
                  </Trans>
                </UI.Div>
              )}
              <UI.ErrorMessages attribute={t('resellable_tickets')} errors={errors.resellable_tickets} />
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.resellable_products, touched: form.touched.resellable_products }}>
              <UI.InputLabel>
                {t('resellable_products')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resellable_products_description')}
              </UI.InputDescription>
              {resellableProducts.map((product) => (
                <UI.Checkbox
                  {...form.checklist('resellable_products', product.id)}
                  key={product.id}
                >
                  {product.title}
                </UI.Checkbox>
              ))}
              {resellableProducts.length === 0 && (
                <UI.Div sc={{ mt: 2 }}>
                  <Trans i18nKey="no_products_created">
                    <UI.Link to={`/${project.id}/events/${event.id}/products/add`} />
                  </Trans>
                </UI.Div>
              )}
              <UI.ErrorMessages attribute={t('resellable_products')} errors={errors.resellable_products} />
            </UI.InputGroup>
          </UI.FormGrid>
        </UI.GridContainer>

        <UI.HR />

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 2fr'], gutter: 1.5 }}>
          <UI.Div>
            <UI.H4>{t('confirmation_texts')}</UI.H4>
            <UI.InputDescription>
              {t('resale_confirmation_texts_description')}
              {' '}
              <Trans i18nKey="view_and_change_default_confirmation_texts">
                <Link to={`/${project.id}/events/${event.id}/settings/confirmation-messages`} />
              </Trans>
            </UI.InputDescription>
          </UI.Div>

          <UI.FormGrid>
            <UI.InputGroup
              sc={{ valid: !errors.confirmation_text, touched: form.touched.confirmation_text }}
            >
              <UI.InputLabel id="ResaleConfirmationTextLabel">
                {t('resale_confirmation_text')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_confirmation_text_description')}
              </UI.InputDescription>
              <UI.Checkbox
                checked={form.values.use_default_confirmation_text}
                onChange={() => toggleDefaultConfirmationText()}
              >
                {t('use_default_confirmation_text')}
              </UI.Checkbox>
              {!form.values.use_default_confirmation_text && (
                <UI.Div sc={{ mt: 2 }}>
                  <TranslatableEditor
                    form={form}
                    name="confirmation_text"
                    defaultLocale={event.locale}
                    title={t('confirmation_text')}
                    attributes={{
                      'aria-labelledby': 'ResaleConfirmationTextLabel',
                    }}
                  />
                </UI.Div>
              )}
              <UI.ErrorMessages
                attribute={t('confirmation_text')}
                errors={errors.confirmation_text}
              />
            </UI.InputGroup>

            <UI.InputGroup
              sc={{ valid: !errors.confirmation_mail_text, touched: form.touched.confirmation_mail_text }}
            >
              <UI.InputLabel id="ResaleConfirmationMailTextLabel">
                {t('resale_confirmation_mail_text')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_confirmation_mail_text_description')}
              </UI.InputDescription>
              <UI.Checkbox
                checked={form.values.use_default_confirmation_mail}
                onChange={() => toggleDefaultConfirmationMail()}
              >
                {t('use_default_confirmation_text')}
              </UI.Checkbox>
              {!form.values.use_default_confirmation_mail && (
                <UI.Div sc={{ mt: 2 }}>
                  <MailEditorLayout
                    editor={(props) => (
                      <TranslatableEditor
                        form={form}
                        name="confirmation_mail_text"
                        title={t('resale_confirmation_mail_text')}
                        defaultLocale={event.locale}
                        options="full"
                        variables={replacementKeys}
                        attributes={{
                          'aria-labelledby': 'ResaleConfirmationMailTextLabel',
                        }}
                        {...props}
                      />
                    )}
                    value={form.values.confirmation_mail_text}
                    event={event}
                    showRegistrationSummary
                    showOrderSummary
                  />
                </UI.Div>
              )}
              <UI.ErrorMessages
                attribute={t('resale_confirmation_mail_text')}
                errors={errors.confirmation_mail_text}
              />
            </UI.InputGroup>

            <UI.HR />

            <UI.InputGroup
              sc={{ valid: !errors.ticket_listed_mail_text, touched: form.touched.ticket_listed_mail_text }}
            >
              <UI.InputLabel id="ResaleTicketListedMailTextLabel">
                {t('resale_ticket_listed_mail_text')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_ticket_listed_mail_text_description')}
              </UI.InputDescription>
              <UI.Div sc={{ mt: 2 }}>
                <MailEditorLayout
                  editor={(props) => (
                    <TranslatableEditor
                      form={form}
                      name="ticket_listed_mail_text"
                      defaultLocale={event.locale}
                      title={t('resale_ticket_listed_mail_text')}
                      options="full"
                      variables={replacementKeys}
                      attributes={{
                        'aria-labelledby': 'ResaleTicketListedMailTextLabel',
                      }}
                      {...props}
                    />
                  )}
                  value={form.values.ticket_listed_mail_text}
                  event={event}
                  showRegistrationSummary
                />
              </UI.Div>
              <UI.ErrorMessages
                attribute={t('resale_ticket_listed_mail_text')}
                errors={errors.ticket_listed_mail_text}
              />
            </UI.InputGroup>

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save_event')}
              loading={loading}
              valid={valid}
            />
          </UI.FormGrid>
        </UI.FormGrid>
      </UI.Card>
    </UI.Form>
  );
};

export default ResaleSettingsPage;
